import { useEffect, useState, useMemo } from 'react';
import {
  ApiModelVersionDataPoint,
  useModelVersionsQuery,
} from 'src/api/queries';
import { AppServices } from 'src/app/routes/utils';
import { sortByAlias } from 'src/utils/sort';
import { useAppContext } from '../AppContext';

export const filterQuickWhenFullExists = <T extends { alias: string }>(
  modelVersions: T[],
): T[] => {
  const fullAliases = new Set(
    modelVersions
      .filter((mv) => mv.alias.endsWith('(Full)'))
      .map((mv) => mv.alias.replace(/ \(Full\)$/, '')),
  );

  return modelVersions.filter((mv) => {
    if (!mv.alias.endsWith('(Quick)')) return true;
    const baseAlias = mv.alias.replace(/ \(Quick\)$/, '');
    return !fullAliases.has(baseAlias);
  });
};

export type ModelVersion = ApiModelVersionDataPoint & { customerName: string };

export type ModelVersionsState = {
  modelVersions: Array<ModelVersion>;
  isLoading: boolean;
  isError: boolean;
};

const INIT_MODEL_VERSIONS_STATE: Readonly<ModelVersionsState> = {
  modelVersions: [],
  isError: false,
  isLoading: false,
};

export const useModelVersions = (
  currentAppService: AppServices | null,
  currentModelVersion: ModelVersion | null,
  setCurrentModelVersion: (modelVersion: ModelVersion | null) => void,
  updateTimeRangeFromModel: (modelVersion: ModelVersion) => void,
  timeRangeLocked: boolean,
) => {
  const { currentCustomer, subscriptionLevels } = useAppContext();

  const [modelVersionsState, setAllModelVersionsState] =
    useState<ModelVersionsState>(INIT_MODEL_VERSIONS_STATE);

  const enabled =
    !!currentCustomer &&
    !!currentAppService &&
    !!subscriptionLevels[currentAppService];

  const hasNoModelVersions = modelVersionsState.modelVersions.length === 0;

  const {
    data: mvData,
    isLoading: mvLoading,
    isError: mvError,
  } = useModelVersionsQuery(
    {
      customer: currentCustomer?.customerName ?? 'missing_customer',
      model_type: currentAppService ?? ('missing_model_Type' as AppServices),
    },
    enabled,
    hasNoModelVersions ? 5000 : false,
  );

  const filteredMvData = useMemo(
    () => filterQuickWhenFullExists(mvData ?? []),
    [mvData],
  );

  // After data has been fetched, update the context for each:
  useEffect(() => {
    if (!enabled) {
      setAllModelVersionsState(INIT_MODEL_VERSIONS_STATE);
      setCurrentModelVersion(null);
      return;
    }

    const modelVersions = filteredMvData.map((d) => ({
      ...d,
      customerName: currentCustomer.customerName,
    }));

    setAllModelVersionsState({
      modelVersions: sortByAlias(modelVersions),
      isLoading: mvLoading,
      isError: mvError,
    });

    if (modelVersions.length > 0) {
      // Only set model if there's no current model or current model is not in the list
      const currentModelInList = currentModelVersion
        ? modelVersions.find(
            (m) =>
              m.modelParametersId === currentModelVersion.modelParametersId,
          )
        : null;

      if (!currentModelInList) {
        const newModelVersion = modelVersions[0];
        setCurrentModelVersion(newModelVersion);
        if (!timeRangeLocked) {
          updateTimeRangeFromModel(newModelVersion);
        }
      }
    } else {
      setCurrentModelVersion(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enabled,
    filteredMvData,
    mvLoading,
    mvError,
    currentCustomer,
    setCurrentModelVersion,
    updateTimeRangeFromModel,
  ]);

  return {
    modelVersionsState,
    setCurrentModelVersion,
  };
};
